// Runs SmartLayer processing off the main thread
// so the dashboard UI never freezes during imports.
//
// Strategy:
//   1. Fetch /smartlayer.js as text and inline into a Blob Worker.
//   2. importScripts inside the blob avoids CORS issues with direct URLs.
//   3. 15-second timeout per call prevents hangs.
//   4. Falls back to main-thread execution if the worker fails.

let worker = null;
let workerReady = false;
let msgId = 0;
const pending = new Map();
const TIMEOUT_MS = 15000;

async function createWorker() {
  if (worker && workerReady) return worker;

  try {
    // Fetch the bundle as text so we can inline it
    const response = await fetch('/smartlayer.js');
    if (!response.ok) throw new Error(`fetch failed: ${response.status}`);
    const bundleText = await response.text();

    const blob = new Blob([`
      ${bundleText}

      self.onmessage = async ({ data: { id, fn, args } }) => {
        try {
          // SmartLayer is defined by the inlined bundle above
          const sl = (typeof SmartLayer !== 'undefined') ? SmartLayer
            : (typeof self.SmartLayer !== 'undefined') ? self.SmartLayer : null;
          if (!sl || typeof sl[fn] !== 'function') {
            self.postMessage({ id, error: 'SmartLayer function not found: ' + fn });
            return;
          }
          const result = sl[fn](...args);
          const resolved = result instanceof Promise ? await result : result;
          self.postMessage({ id, result: resolved });
        } catch (err) {
          self.postMessage({ id, error: err.message || String(err) });
        }
      };
    `], { type: 'application/javascript' });

    const blobUrl = URL.createObjectURL(blob);
    worker = new Worker(blobUrl);

    worker.onmessage = ({ data: { id, result, error } }) => {
      const entry = pending.get(id);
      if (!entry) return;
      pending.delete(id);
      clearTimeout(entry.timer);
      if (error) entry.reject(new Error(error));
      else entry.resolve(result);
    };

    worker.onerror = (e) => {
      console.warn('[SmartLayerWorker] worker error:', e.message);
    };

    workerReady = true;
    console.log('[SmartLayerWorker] ready');
    return worker;
  } catch (e) {
    console.warn('[SmartLayerWorker] worker creation failed:', e.message);
    workerReady = false;
    return null;
  }
}

function mainThreadFallback(fnName, args) {
  console.warn('[SmartLayerWorker] fallback used:', fnName);
  const sl = window.SmartLayer;
  if (!sl || typeof sl[fnName] !== 'function') {
    return Promise.reject(new Error('SmartLayer not available: ' + fnName));
  }
  const result = sl[fnName](...args);
  return result instanceof Promise ? result : Promise.resolve(result);
}

export function callSmartLayer(fnName, ...args) {
  return new Promise(async (resolve, reject) => {
    const w = await createWorker();

    if (!w) {
      try {
        const result = await mainThreadFallback(fnName, args);
        resolve(result);
      } catch (e) {
        reject(e);
      }
      return;
    }

    const id = ++msgId;
    const timer = setTimeout(() => {
      pending.delete(id);
      console.warn('[SmartLayerWorker] timeout:', fnName);
      reject(new Error('SmartLayer worker timeout: ' + fnName));
    }, TIMEOUT_MS);

    pending.set(id, { resolve, reject, timer });
    w.postMessage({ id, fn: fnName, args });
  });
}

export async function warmUpWorker() {
  try {
    await createWorker();
  } catch (_) {}
}
